import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectClient,
  selectMaybeClientPartner,
  selectTraditionalLTCQuotesError,
  selectTraditionalLTCQuotesIsLoading,
  postTraditionalLTCQuotesQueryRequest,
} from '@client/model';
import { calculateAge, zipCodeToState } from '@shared';
import { getMaritalClassForTraditionalLTC } from '@client/hooks/useShopInsuranceState';
import type { PaymentOption } from '@client/components/ShopInsuranceModal';

interface UseTraditionalLTCQuoteFetchingProps {
  open: boolean;
  paymentOption: PaymentOption;
  selectedBuyerOption: {
    key: '1-buyer' | '2-buyer';
    label: string;
    maritalClass: string;
  };
  hasPartnerWithDemographics: boolean;
  advisorId: string;
}

const ELIGIBLE_PAYMENT_OPTIONS = ['Annually', 'Monthly'];

export function useTraditionalLTCQuoteFetching({
  open,
  paymentOption,
  selectedBuyerOption,
  hasPartnerWithDemographics,
  advisorId,
}: UseTraditionalLTCQuoteFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const isLoadingTraditionalLTC = useSelector(selectTraditionalLTCQuotesIsLoading);
  const traditionalLTCError = useSelector(selectTraditionalLTCQuotesError);

  const fetchedQuoteConfigs = useRef<Set<string>>(new Set());

  // Fetch traditional LTC quotes
  useEffect(() => {
    if (open && client?.intakeSurvey && !isLoadingTraditionalLTC) {
      const eligiblePaymentOptions = ELIGIBLE_PAYMENT_OPTIONS;

      const cacheKey =
        hasPartnerWithDemographics && eligiblePaymentOptions.includes(paymentOption)
          ? `${paymentOption}-${selectedBuyerOption.key}`
          : paymentOption;

      if (
        eligiblePaymentOptions.includes(paymentOption) &&
        !fetchedQuoteConfigs.current.has(cacheKey)
      ) {
        fetchedQuoteConfigs.current.add(cacheKey);

        const dateOfBirth = new Date(client.intakeSurvey.clientBirthDate);
        const age = calculateAge(dateOfBirth);

        const isTobacco = (user: Client) => {
          return (
            user.intakeSurvey.clientSmokerEver === 'Yes' ||
            user.intakeSurvey.clientSmokedRecently === 'Yes'
          );
        };

        const clientTobaccoStatus = isTobacco(client) ? 'Tobacco' : 'Non-Tobacco';
        const underwritingClass = clientTobaccoStatus === 'Tobacco' ? 'Standard' : 'Preferred';
        const gender = client.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';
        const state = zipCodeToState(client.intakeSurvey.clientZipCode);

        const maritalClass = getMaritalClassForTraditionalLTC(
          paymentOption,
          selectedBuyerOption,
          hasPartnerWithDemographics,
          client.intakeSurvey.clientMaritalStatus,
        );

        const payload = {
          primaryAge: age,
          primaryGender: gender,
          maritalClass: maritalClass,
          usaState: state.stateAbbreviation,
          premiumFrequency:
            paymentOption === 'Annually'
              ? 'Annually'
              : paymentOption === 'Monthly'
                ? 'Monthly'
                : 'Annually',
          underwritingClass: underwritingClass,
          advisorId: advisorId,
        };

        dispatch(postTraditionalLTCQuotesQueryRequest(payload));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    client,
    clientPartner,
    paymentOption,
    selectedBuyerOption,
    isLoadingTraditionalLTC,
    advisorId,
  ]);

  // Clear cache on error
  useEffect(() => {
    if (traditionalLTCError) {
      fetchedQuoteConfigs.current.clear();
    }
  }, [traditionalLTCError]);

  // Clear cache when dependencies change
  useEffect(() => {
    fetchedQuoteConfigs.current.clear();
  }, [selectedBuyerOption, hasPartnerWithDemographics, paymentOption]);

  return {
    isLoadingTraditionalLTC,
    traditionalLTCError,
  };
}
